import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, Clock, Mail, Calendar, MapPin, Users, ArrowLeft, Loader2, ExternalLink, Gift } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const UnifiedApprovalsDashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contributions'); // 'contributions', 'invitations', 'cohost-roles', 'wishlist'
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [cohostRoleRequests, setCohostRoleRequests] = useState([]);
  const [wishlistRequests, setWishlistRequests] = useState([]);
  const [wishlistContributions, setWishlistContributions] = useState([]);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch pending contribution approvals
      try {
        const approvalsResponse = await axios.get(`${API}/my-pending-approvals`, {
          withCredentials: true
        });
        setPendingApprovals(approvalsResponse.data.pending_approvals || []);
      } catch (err) {
        console.log('Event contribution approvals not available:', err.message);
        setPendingApprovals([]);
      }

      // Fetch co-host invitations
      try {
        const invitationsResponse = await axios.get(`${API}/my-cohost-invitations`, {
          withCredentials: true
        });
        setInvitations(invitationsResponse.data.invitations || []);
      } catch (err) {
        console.log('Co-host invitations not available:', err.message);
        setInvitations([]);
      }

      // Fetch co-host role approval requests
      try {
        const cohostRoleResponse = await axios.get(`${API}/my-cohost-role-approvals`, {
          withCredentials: true
        });
        setCohostRoleRequests(cohostRoleResponse.data.pending_cohost_requests || []);
      } catch (err) {
        console.log('Co-host role requests not available:', err.message);
        setCohostRoleRequests([]);
      }

      // Fetch wishlist access requests
      try {
        const wishlistResponse = await axios.get(`${API}/wishlists/pending-approvals`, {
          withCredentials: true
        });
        setWishlistRequests(wishlistResponse.data.requests || []);
      } catch (err) {
        console.log('Wishlist access requests not available:', err.message);
        setWishlistRequests([]);
      }

      // Fetch wishlist contribution approvals
      try {
        console.log('Fetching wishlist contribution approvals...');
        const wishlistContribResponse = await axios.get(`${API}/wishlists/contribution-approvals`, {
          withCredentials: true
        });
        console.log('Wishlist contributions response:', wishlistContribResponse.data);
        setWishlistContributions(wishlistContribResponse.data.pending_contributions || []);
      } catch (err) {
        console.log('Wishlist contribution approvals not available:', err.message);
        setWishlistContributions([]);
      }
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveContribution = async (eventId, rsvpId) => {
    setProcessingId(rsvpId);
    try {
      await axios.put(
        `${API}/events/${eventId}/verify-contribution/${rsvpId}`,
        { status: 'verified' },
        { withCredentials: true }
      );
      toast.success('Contribution approved successfully!');
      fetchData(); // Refresh to remove from pending list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve contribution');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectContribution = async (eventId, rsvpId) => {
    if (!window.confirm('Are you sure you want to reject this contribution?')) {
      return;
    }
    
    setProcessingId(rsvpId);
    try {
      await axios.put(
        `${API}/events/${eventId}/verify-contribution/${rsvpId}`,
        { status: 'rejected' },
        { withCredentials: true }
      );
      toast.success('Contribution rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject contribution');
    } finally {
      setProcessingId(null);
    }
  };

  const handleAcceptInvitation = async (eventId) => {
    setProcessingId(eventId);
    try {
      await axios.post(
        `${API}/events/${eventId}/accept-cohost-invitation`,
        {},
        { withCredentials: true }
      );
      toast.success('Co-host invitation accepted!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setProcessingId(null);
    }
  };

  const handleApproveCohostRole = async (eventId, cohostId) => {
    setProcessingId(cohostId);
    try {
      await axios.put(
        `${API}/events/${eventId}/co-host-role/${cohostId}`,
        { action: 'approve' },
        { withCredentials: true }
      );
      toast.success('Co-host request approved!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve co-host request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectCohostRole = async (eventId, cohostId) => {
    if (!window.confirm('Are you sure you want to reject this co-host request?')) {
      return;
    }
    
    setProcessingId(cohostId);
    try {
      await axios.put(
        `${API}/events/${eventId}/co-host-role/${cohostId}`,
        { action: 'reject' },
        { withCredentials: true }
      );
      toast.success('Co-host request rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject co-host request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleApproveWishlistAccess = async (wishlistId, requestId) => {
    setProcessingId(requestId);
    try {
      await axios.post(
        `${API}/wishlists/${wishlistId}/approve-access/${requestId}`,
        { action: 'approve' },
        { withCredentials: true }
      );
      toast.success('Wishlist access granted for 96 hours!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve access');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectWishlistAccess = async (wishlistId, requestId) => {
    if (!window.confirm('Are you sure you want to reject this access request?')) {
      return;
    }
    
    setProcessingId(requestId);
    try {
      await axios.post(
        `${API}/wishlists/${wishlistId}/approve-access/${requestId}`,
        { action: 'reject' },
        { withCredentials: true }
      );
      toast.success('Access request rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject access');
    } finally {
      setProcessingId(null);
    }
  };

  const handleApproveWishlistContribution = async (wishlistId, contributionId) => {
    setProcessingId(contributionId);
    try {
      await axios.put(
        `${API}/wishlists/${wishlistId}/contributions/${contributionId}/verify`,
        { status: 'verified' },
        { withCredentials: true }
      );
      toast.success('Contribution verified successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify contribution');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectWishlistContribution = async (wishlistId, contributionId) => {
    if (!window.confirm('Are you sure you want to reject this contribution?')) {
      return;
    }
    
    setProcessingId(contributionId);
    try {
      await axios.put(
        `${API}/wishlists/${wishlistId}/contributions/${contributionId}/verify`,
        { status: 'rejected' },
        { withCredentials: true }
      );
      toast.success('Contribution rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject contribution');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-contribo-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header - Mobile Optimized */}
      <div className="bg-gradient-to-r from-contribo-navy to-contribo-gold text-white py-4 px-2">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-xl font-bold mb-1">Approvals Dashboard</h1>
          <p className="text-white text-opacity-90 text-xs">
            Manage approvals and invitations
          </p>
        </div>
      </div>

      {/* Tabs - Mobile Optimized - 4 columns */}
      <div className="max-w-6xl mx-auto px-2 mt-4">
        <div className="grid grid-cols-4 gap-1 mb-4">
          <Button
            onClick={() => setActiveTab('contributions')}
            className={`text-[9px] px-1 py-2 ${activeTab === 'contributions' ? 'bg-contribo-navy' : 'bg-gray-300 text-gray-700'}`}
          >
            <CheckCircle className="mr-0.5 h-3 w-3" />
            Contrib ({pendingApprovals.length + wishlistContributions.length})
          </Button>
          <Button
            onClick={() => setActiveTab('cohost-roles')}
            className={`text-[9px] px-1 py-2 ${activeTab === 'cohost-roles' ? 'bg-orange-500' : 'bg-gray-300 text-gray-700'}`}
          >
            <Users className="mr-0.5 h-3 w-3" />
            Co-Host ({cohostRoleRequests.length})
          </Button>
          <Button
            onClick={() => setActiveTab('invitations')}
            className={`text-[9px] px-1 py-2 ${activeTab === 'invitations' ? 'bg-contribo-gold' : 'bg-gray-300 text-gray-700'}`}
          >
            <Mail className="mr-0.5 h-3 w-3" />
            Invites ({invitations.length})
          </Button>
          <Button
            onClick={() => setActiveTab('wishlist')}
            className={`text-[9px] px-1 py-2 ${activeTab === 'wishlist' ? 'bg-purple-600' : 'bg-gray-300 text-gray-700'}`}
          >
            <Gift className="mr-0.5 h-3 w-3" />
            Wishlist ({wishlistRequests.length})
          </Button>
        </div>

        {/* Contribution Approvals Tab */}
        {activeTab === 'contributions' && (
          <div className="space-y-4">
            {/* Event Contributions Section */}
            {pendingApprovals.length > 0 && (
              <>
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-yellow-500" />
                  Event Contributions
                </h3>
                {pendingApprovals.map((approval) => (
                <Card key={approval.rsvp_id} className="border-l-4 border-l-yellow-500">
                  <CardHeader className="bg-yellow-50 py-3 px-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-sm text-gray-900 font-bold">{approval.event_title}</CardTitle>
                        <div className="flex flex-col gap-1 mt-1 text-xs text-gray-600">
                          <span className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {approval.event_date} at {approval.event_time}
                          </span>
                          <Badge className="bg-blue-500 text-xs w-fit">Role: {approval.user_role}</Badge>
                        </div>
                      </div>
                      <Badge className="bg-yellow-500 text-white text-xs">
                        <Clock className="h-2 w-2 mr-1" />
                        Pending
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4 px-3 pb-3">
                    <div className="space-y-2 mb-3 text-xs">
                      <div>
                        <p className="text-gray-600">Guest:</p>
                        <p className="font-semibold text-gray-900">{approval.guest_name}</p>
                        <p className="text-gray-600">{approval.guest_email}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Amount:</p>
                        <p className="font-bold text-green-600 text-lg">₹{approval.total_amount}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Transaction ID:</p>
                        <p className="font-semibold text-gray-900">{approval.transaction_id || 'N/A'}</p>
                      </div>
                      {approval.transaction_date && (
                        <div>
                          <p className="text-gray-600">Date:</p>
                          <p className="font-semibold text-gray-900">{approval.transaction_date}</p>
                        </div>
                      )}
                    </div>

                    {/* Contributions Breakdown */}
                    {approval.contributions && approval.contributions.length > 0 && (
                      <div className="mb-3 p-2 bg-gray-50 rounded">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Goals:</p>
                        {approval.contributions.map((contrib, idx) => (
                          <div key={idx} className="flex justify-between text-xs">
                            <span className="text-gray-600">{contrib.goal_title || contrib.custom_goal_title}</span>
                            <span className="font-semibold text-gray-900">₹{contrib.amount}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Payment Proof */}
                    {approval.payment_proof && (
                      <div className="mb-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(approval.payment_proof, '_blank')}
                          className="text-blue-600 text-xs py-1 h-auto"
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          View Proof
                        </Button>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApproveContribution(approval.event_id, approval.rsvp_id)}
                        disabled={processingId === approval.rsvp_id}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-xs py-2"
                      >
                        {processingId === approval.rsvp_id ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : (
                          <CheckCircle className="mr-1 h-3 w-3" />
                        )}
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleRejectContribution(approval.event_id, approval.rsvp_id)}
                        disabled={processingId === approval.rsvp_id}
                        variant="outline"
                        className="flex-1 border-red-500 text-red-500 hover:bg-red-50 text-xs py-2"
                      >
                        <XCircle className="mr-1 h-3 w-3" />
                        Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </>
            )}

            {/* Wishlist Contributions Section */}
            {wishlistContributions.length > 0 ? (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Gift className="h-5 w-5 text-purple-600" />
                  Wishlist Contributions ({wishlistContributions.length})
                </h3>
                <div className="space-y-4">
                  {wishlistContributions.map((contrib) => (
                    <Card key={contrib.id} className="border-l-4 border-l-purple-500">
                      <CardHeader className="bg-purple-50 py-3 px-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-sm text-gray-900 font-bold">{contrib.wishlist_name}</CardTitle>
                            <p className="text-xs text-gray-600 mt-1">Item: {contrib.item_name}</p>
                          </div>
                          <Badge className="bg-purple-500 text-white text-xs">
                            <Clock className="h-2 w-2 mr-1" />
                            Pending
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4 px-3 pb-3">
                        <div className="space-y-2 mb-3 text-xs">
                          <div className="bg-purple-50 border border-purple-200 rounded p-2">
                            <p className="text-xs text-purple-900 font-semibold mb-1">Contributor Details:</p>
                            <p className="font-semibold text-gray-900">{contrib.contributor_name}</p>
                            {contrib.contributor_email && (
                              <p className="text-gray-600">{contrib.contributor_email}</p>
                            )}
                          </div>
                          <div className="bg-green-50 border border-green-200 rounded p-2">
                            <p className="text-xs text-green-900 font-semibold mb-1">Payment Details:</p>
                            <p className="font-bold text-green-700">Amount: ₹{contrib.amount?.toLocaleString()}</p>
                            <p className="text-gray-600">Transaction ID: {contrib.transaction_id || 'N/A'}</p>
                            <p className="text-gray-600">Date: {contrib.transaction_date || 'N/A'}</p>
                          </div>
                          {contrib.item_price && (
                            <p className="text-gray-500">Item Price: ₹{contrib.item_price?.toLocaleString()}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApproveWishlistContribution(contrib.wishlist_id, contrib.id)}
                            disabled={processingId === contrib.id}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-xs py-2"
                          >
                            {processingId === contrib.id ? (
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            ) : (
                              <CheckCircle className="mr-1 h-3 w-3" />
                            )}
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleRejectWishlistContribution(contrib.wishlist_id, contrib.id)}
                            disabled={processingId === contrib.id}
                            variant="outline"
                            className="flex-1 border-red-500 text-red-500 hover:bg-red-50 text-xs py-2"
                          >
                            <XCircle className="mr-1 h-3 w-3" />
                            Reject
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-700 flex items-center gap-2">
                  <Gift className="h-4 w-4" />
                  No pending wishlist contributions at the moment.
                </p>
              </div>
            )}

            {/* Empty State - Only show when BOTH are empty */}
            {pendingApprovals.length === 0 && wishlistContributions.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">All Caught Up!</h3>
                  <p className="text-gray-500">No pending contribution approvals at the moment.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Co-host Role Approvals Tab */}
        {activeTab === 'cohost-roles' && (
          <div className="space-y-4">
            {cohostRoleRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Pending Requests</h3>
                  <p className="text-gray-500">No co-host role requests at the moment.</p>
                </CardContent>
              </Card>
            ) : (
              cohostRoleRequests.map((request) => (
                <Card key={request.co_host_id} className="border-l-4 border-l-orange-500">
                  <CardHeader className="bg-orange-50 py-3 px-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-sm text-gray-900 font-bold">{request.event_title}</CardTitle>
                        <div className="flex flex-col gap-1 mt-1 text-xs text-gray-600">
                          <span className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {request.event_date} at {request.event_time}
                          </span>
                          <span className="text-xs text-gray-500">Event Code: {request.event_code}</span>
                        </div>
                      </div>
                      <Badge className="bg-orange-500 text-white text-xs">
                        <Clock className="h-2 w-2 mr-1" />
                        Pending
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4 px-3 pb-3">
                    <div className="space-y-2 mb-3 text-xs">
                      <div className="bg-blue-50 border border-blue-200 rounded p-2">
                        <p className="text-xs text-blue-900 font-semibold mb-1">Requester Details:</p>
                        <p className="font-semibold text-gray-900">{request.requester_name}</p>
                        <p className="text-gray-600">{request.requester_email}</p>
                      </div>
                      
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                        <p className="text-xs text-gray-700">
                          <strong>Request Type:</strong> {request.request_type === 'self_requested' ? 'User requested via event code' : 'Invited'}
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={() => handleApproveCohostRole(request.event_id, request.co_host_id)}
                        disabled={processingId === request.co_host_id}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-xs"
                      >
                        {processingId === request.co_host_id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="mr-1 h-3 w-3" />
                            Approve
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleRejectCohostRole(request.event_id, request.co_host_id)}
                        disabled={processingId === request.co_host_id}
                        variant="outline"
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50 text-xs"
                      >
                        {processingId === request.co_host_id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <XCircle className="mr-1 h-3 w-3" />
                            Reject
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Co-host Invitations Tab */}
        {activeTab === 'invitations' && (
          <div className="space-y-4">
            {invitations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Mail className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Pending Invitations</h3>
                  <p className="text-gray-500">You don&apos;t have any pending co-host invitations.</p>
                </CardContent>
              </Card>
            ) : (
              invitations.map((invitation) => (
                <Card key={invitation.event_id} className="border-l-4 border-l-contribo-gold">
                  <CardHeader className="bg-amber-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl text-gray-900">{invitation.event_title}</CardTitle>
                        <p className="text-sm text-gray-600 mt-1">
                          You&apos;ve been invited by <span className="font-semibold">{invitation.host_name}</span>
                        </p>
                      </div>
                      <Badge className="bg-contribo-gold text-white">
                        <Mail className="h-3 w-3 mr-1" />
                        Invited
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center text-gray-700">
                        <Calendar className="h-5 w-5 mr-2 text-contribo-gold" />
                        <span>{invitation.event_date} at {invitation.event_time}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <MapPin className="h-5 w-5 mr-2 text-contribo-gold" />
                        <span>{invitation.event_location}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Users className="h-5 w-5 mr-2 text-contribo-gold" />
                        <span>Host: {invitation.host_email}</span>
                      </div>
                    </div>

                    <div className="p-3 bg-blue-50 rounded mb-4">
                      <p className="text-sm text-blue-900 font-semibold mb-1">Co-host Permissions:</p>
                      <ul className="text-xs text-blue-800 space-y-1">
                        <li>✅ Approve/reject contributions</li>
                        <li>✅ Share event & download tracker</li>
                        <li>⚠️ Edit/Delete event (requires host approval)</li>
                      </ul>
                    </div>

                    <Button
                      onClick={() => handleAcceptInvitation(invitation.event_id)}
                      disabled={processingId === invitation.event_id}
                      className="w-full bg-contribo-gold hover:bg-yellow-600"
                    >
                      {processingId === invitation.event_id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Accepting...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Accept Invitation
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Wishlist Access Approvals Tab */}
        {activeTab === 'wishlist' && (
          <div className="space-y-4">
            {wishlistRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Gift className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Pending Requests</h3>
                  <p className="text-gray-500">No one has requested access to your wishlists.</p>
                </CardContent>
              </Card>
            ) : (
              wishlistRequests.map((request) => (
                <Card key={request.id} className="border-l-4 border-l-purple-500">
                  <CardHeader className="bg-purple-50 py-3 px-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-sm text-gray-900 font-bold">{request.wishlist_name}</CardTitle>
                        <p className="text-xs text-gray-600 mt-1">Wishlist Access Request</p>
                      </div>
                      <Badge className="bg-purple-500 text-white text-xs">
                        <Clock className="h-2 w-2 mr-1" />
                        Pending
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4 px-3 pb-3">
                    <div className="space-y-2 mb-3 text-xs">
                      <div className="bg-purple-50 border border-purple-200 rounded p-2">
                        <p className="text-xs text-purple-900 font-semibold mb-1">Requester Details:</p>
                        <p className="font-semibold text-gray-900">{request.requester_name}</p>
                        <p className="text-gray-600">{request.requester_email}</p>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded p-2">
                        <p className="text-xs text-blue-700">
                          <strong>Note:</strong> Once approved, they can view and contribute to your wishlist for 96 hours.
                        </p>
                      </div>

                      {request.requested_at && (
                        <div className="text-gray-500 text-xs">
                          Requested: {new Date(request.requested_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={() => handleApproveWishlistAccess(request.wishlist_id, request.id)}
                        disabled={processingId === request.id}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-xs"
                      >
                        {processingId === request.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="mr-1 h-3 w-3" />
                            Approve
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleRejectWishlistAccess(request.wishlist_id, request.id)}
                        disabled={processingId === request.id}
                        variant="outline"
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50 text-xs"
                      >
                        {processingId === request.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <XCircle className="mr-1 h-3 w-3" />
                            Reject
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default UnifiedApprovalsDashboard;
